import { z } from 'zod';
import { CachedDataSchema } from 'packages/lib/api/annotations-app-api/contract/cached-data-type';

export const LidarBoxQualityResultSchema = z.object({
  total_annotations: z.coerce.number().int().min(0),
  true_positives_perfect: z.coerce.number().int(),
  true_positives_corrected: z.coerce.number().int(),
  false_positives: z.coerce.number().int(),
  false_negatives: z.coerce.number().int(),
  avg_3d_iou: z.coerce.number().min(0).max(1),
  total_attribute_errors: z.coerce.number().int().min(0),
});
export type LidarBoxQualityResult = z.infer<typeof LidarBoxQualityResultSchema>;

export const SegmentationQualityResultSchema = z.object({
  total_segments: z.coerce.number().int().min(0),
  mean_iou_score: z.coerce.number().min(0).max(1),
  false_positive_masks: z.coerce.number().int(),
  false_negative_masks: z.coerce.number().int(),
  corrected_masks: z.coerce.number().int(),
});
export type SegmentationQualityResult = z.infer<
  typeof SegmentationQualityResultSchema
>;

export const VideoEventQualityResultSchema = z.object({
  total_frames_annotated: z.coerce.number().int(),
  frame_accuracy_pct: z.coerce.number().min(0).max(100),
  avg_attribute_errors_per_frame: z.coerce.number().min(0),
});
export type VideoEventQualityResult = z.infer<
  typeof VideoEventQualityResultSchema
>;

export const EnrichmentQualityResultSchema = z.object({
  total_enrichments: z.coerce.number().int(),
  classification_accuracy_pct: z.coerce.number().min(0).max(100),
  missed_tags: z.coerce.number().int(),
  hallucinated_tags: z.coerce.number().int(),
  total_attribute_errors: z.coerce.number().int(),
});
export type EnrichmentQualityResult = z.infer<
  typeof EnrichmentQualityResultSchema
>;

export const RawUserQualityMetricsSchema = z.object({
  lidar_box: LidarBoxQualityResultSchema.nullable().optional(),
  instance_seg: SegmentationQualityResultSchema.nullable().optional(),
  sem_seg: SegmentationQualityResultSchema.nullable().optional(),
  lidar_seg: SegmentationQualityResultSchema.nullable().optional(),
  video_event: VideoEventQualityResultSchema.nullable().optional(),
  enrichment: EnrichmentQualityResultSchema.nullable().optional(),
  overall_accuracy: z.number().optional(),
  total_objects: z.number().optional(),
});
export type RawUserQualityMetrics = z.infer<typeof RawUserQualityMetricsSchema>;
export const UserQualityMetricsSchema = CachedDataSchema(
  RawUserQualityMetricsSchema
);
export type UserQualityMetrics = z.infer<typeof UserQualityMetricsSchema>;

/**
 * A discriminated union of all possible workforce quality metric shapes.
 * This is used to validate the response from the aggregate quality endpoint.
 */
export const WorkforceQualityResultSchema = z.discriminatedUnion(
  'workflow_name',
  [
    LidarBoxQualityResultSchema.extend({
      workflow_name: z.literal('LidarBox'),
    }),
    SegmentationQualityResultSchema.extend({
      workflow_name: z.enum(['instance_seg', 'sem_seg', 'lidar_seg']),
    }),
    VideoEventQualityResultSchema.extend({
      workflow_name: z.literal('VideoEvent'),
    }),
    EnrichmentQualityResultSchema.extend({
      workflow_name: z.literal('Enrichment'),
    }),
  ]
);

/**
 * The response schema for the workforce quality metrics endpoint (aggregate).
 */
export const RawWorkforceQualityResponseSchema = z.array(
  WorkforceQualityResultSchema
);
export const WorkforceQualityResponseSchema = CachedDataSchema(
  RawWorkforceQualityResponseSchema
);
export type WorkforceQualityResponse = z.infer<
  typeof WorkforceQualityResponseSchema
>;

/**
 * The schema for a single item in the response from the team-level
 * match score endpoint, which returns per-user metrics.
 */
export const TeamMatchScoreItemSchema = UserQualityMetricsSchema;

/**
 * The response schema for the team-level match score endpoint, which
 * returns an array of per-user performance metrics.
 */
export const TeamMatchScoreResponseSchema = z.array(TeamMatchScoreItemSchema);
